import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCompleteWheelOptionsIfNeeded, fetchCompleteWheelsIfNeeded } from '../../../modules/actions/standort/completeWheels';
import { completeWheelOptionsResponseSelector, completeWheelsResponseSelector } from '../../../modules/selectors/standort/completeWheels';
import { connect } from 'react-redux';
import { frontloadConnect } from 'react-frontload';
import { useLocation } from 'react-router-dom';
import { parseSearch, serializeSearchParams } from '../../../helpers/UrlHelper';
import { castArray, compact, difference, isEmpty } from 'lodash';
import BreadCrumbSelector from '../../components/BreadCrumb/BreadCrumbSelector';
import { useStandortHistory } from '../../components/VHost/StandortHistory';
import OptionsSidebar from '../../components/OptionsSidebar/OptionsSidebar';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import InlineSpinner from '../../components/Spinner/InlineSpinner';
import Pagination from '../../components/Pagination/Pagination';
import ProductListFooter from '../../components/product-list/footer';
import CompleteWheelList from './CompleteWheelList';
import FilterLayout from '../../components/Filter/FilterLayout';
import { gripSymbols, tireLabels } from '../../../helpers/tiresHelper';
import PriceRangeFilter from '../../components/Filter/PriceRangeFilter/PriceRangeFilter';
import Image from '../../components/Image/Image';
import Input from '../../components/form-elements/Input';
import { DimensionFilters } from '../../components/filter-fragments/DimensionFilters';
import CheckboxGroup from '../../components/form-elements/CheckboxGroup';
import Button from '../../components/form-elements/Button';

const CheckBoxFilters = ({ options, selectedOptions, onFilterChanged, onSelectedOptionsChange }) => {
    //Check for invalid multiple choice options
    useEffect(() => {
        const selectedOptionsUpdate = Object.fromEntries(
            ['tragfaehigkeit', 'geschwindigkeitsindex', 'reifenHersteller', 'felgenHersteller'].flatMap(arrKey => {
                const available = options[arrKey];
                const selected = selectedOptions[arrKey];
                const invalidOptions = difference(selected, available);
                if (!isEmpty(invalidOptions)) {
                    const validOptions = difference(selected, invalidOptions);
                    return [[arrKey, validOptions]];
                }
                return [];
            })
        );

        if (!isEmpty(selectedOptionsUpdate)) {
            onSelectedOptionsChange({ ...selectedOptions, ...selectedOptionsUpdate });
        }
    }, [onSelectedOptionsChange, options, selectedOptions]);
    return (
        <>
            <FilterLayout textHeader="Tragfähigkeit">
                <CheckboxGroup
                    name="tragfaehigkeit"
                    options={options.tragfaehigkeit.map(option => ({ label: option, value: option }))}
                    checkedValues={selectedOptions.tragfaehigkeit}
                    onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                />
            </FilterLayout>
            <FilterLayout textHeader="Geschwindigkeit">
                <CheckboxGroup
                    name="geschwindigkeitsindex"
                    options={options.geschwindigkeitsindex.map(option => ({ label: option, value: option }))}
                    checkedValues={selectedOptions.geschwindigkeitsindex}
                    onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                />
            </FilterLayout>
            <FilterLayout textHeader="Reifenhersteller">
                <CheckboxGroup
                    name="reifenHersteller"
                    options={options.reifenHersteller.map(option => ({ label: option, value: option }))}
                    checkedValues={selectedOptions.reifenHersteller}
                    onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                />
            </FilterLayout>
            <FilterLayout textHeader="Felgenhersteller">
                <CheckboxGroup
                    name="felgenHersteller"
                    options={options.felgenHersteller.map(option => ({ label: option, value: option }))}
                    checkedValues={selectedOptions.felgenHersteller}
                    onChange={({ name, values }) => onFilterChanged({ name, value: values })}
                />
            </FilterLayout>
        </>
    );
};

function FilterBoxen({ selectedOptions, onSelectedOptionsChange, completeWheelOptionsResponse }) {
    const [hsn, setHsn] = useState(selectedOptions.hsn);
    const [tsn, setTsn] = useState(selectedOptions.tsn);

    useEffect(() => {
        setHsn(selectedOptions.hsn);
    }, [selectedOptions.hsn]);
    useEffect(() => {
        setTsn(selectedOptions.tsn);
    }, [selectedOptions.tsn]);

    const onFilterChanged = useCallback(
        ({ name, value }) => {
            onSelectedOptionsChange({ ...selectedOptions, [name]: value });
        },
        [onSelectedOptionsChange, selectedOptions]
    );

    return (
        <>
            <FilterLayout textHeader="Schlüsselnummer">
                <Input name="hsn" label="HSN" value={hsn} onChange={({ value }) => setHsn(value)} className="" maxLength={4} />
                <Input name="tsn" label="TSN" value={tsn} onChange={({ value }) => setTsn(value)} className="" maxLength={3} />
                <Button
                    onClick={() => {
                        onSelectedOptionsChange({ ...selectedOptions, hsn, tsn });
                    }}
                    fullWidth
                    className="uk-margin-small-top"
                >
                    Filter anwenden
                </Button>
            </FilterLayout>
            <FilterLayout textHeader="Breite/Höhe/Zoll">
                <ReduxSuspense response={completeWheelOptionsResponse} fallback={<InlineSpinner />} error={null}>
                    {options => (
                        <DimensionFilters
                            options={options}
                            selectedOptions={selectedOptions}
                            onFilterChanged={onFilterChanged}
                            onSelectedOptionsChange={onSelectedOptionsChange}
                        />
                    )}
                </ReduxSuspense>
            </FilterLayout>
            <ReduxSuspense
                response={completeWheelOptionsResponse}
                fallback={
                    <FilterLayout textHeader="Preis">
                        <InlineSpinner />
                    </FilterLayout>
                }
                error={null}
            >
                {options => {
                    if (options.preisAb >= options.preisBis) return null;

                    const currentMinPrice = parseInt(selectedOptions.preisAb) || options.preisAb;
                    const currentMaxPrice = parseInt(selectedOptions.preisBis) || options.preisBis;

                    return (
                        <FilterLayout textHeader="Preis">
                            <PriceRangeFilter
                                currentMinPrice={currentMinPrice}
                                currentMaxPrice={currentMaxPrice}
                                minPrice={options.preisAb}
                                maxPrice={options.preisBis}
                                onFilterChanged={({ minPrice: preisAb, maxPrice: preisBis }) => {
                                    // Check if preisAb has changed
                                    if (preisAb !== currentMinPrice) {
                                        // Reset preisAb if at range limit, otherwise set new value.
                                        onFilterChanged({
                                            name: 'preisAb',
                                            value: preisAb === options.preisAb ? '' : preisAb,
                                        });
                                    }
                                    // Check if preisBis has changed
                                    if (preisBis !== currentMaxPrice) {
                                        // Reset preisBis if at range limit, otherwise set new value.
                                        onFilterChanged({
                                            name: 'preisBis',
                                            value: preisBis === options.preisBis ? '' : preisBis,
                                        });
                                    }
                                }}
                            />
                        </FilterLayout>
                    );
                }}
            </ReduxSuspense>
            <ReduxSuspense
                response={completeWheelOptionsResponse}
                fallback={
                    // TODO: when CheckBoxFilter uses FilterLayout, we can simplify this fallback.
                    <>
                        <FilterLayout textHeader="Zustellung">
                            <InlineSpinner />
                        </FilterLayout>
                        <FilterLayout textHeader="Tragfähigkeit">
                            <InlineSpinner />
                        </FilterLayout>
                        <FilterLayout textHeader="Geschwindigkeit">
                            <InlineSpinner />
                        </FilterLayout>
                        <FilterLayout textHeader="Hersteller">
                            <InlineSpinner />
                        </FilterLayout>
                    </>
                }
                error={null}
            >
                {options => (
                    <CheckBoxFilters
                        options={options}
                        selectedOptions={selectedOptions}
                        onFilterChanged={onFilterChanged}
                        onSelectedOptionsChange={onSelectedOptionsChange}
                    />
                )}
            </ReduxSuspense>
            <FilterLayout textHeader="Reifenlabel">
                {tireLabels.map(({ imgSrc, alt, label }, i) => (
                    <div key={i} className='tireLabels_icons'>
                        <Image resizeWidth={20} className="uk-padding-small" src={imgSrc} alt={alt} />
                        <span>{label}</span>
                    </div>
                ))}
                {gripSymbols.map(({ imgSrc, alt, label }, i) => (
                    <div key={i} className='gripSymbols_icons'>
                        <Image className="uk-padding-small" resizeWidth={20} src={imgSrc} alt={alt} />
                        <span>{label}</span>
                    </div>
                ))}
            </FilterLayout>
        </>
    );
}

function CompleteWheelsContent({ selectedOptions, completeWheelsResponse, completeWheelOptionsResponse }) {
    const history = useStandortHistory();

    const onSelectedOptionsChange = useCallback(
        selectedOptions => {
            history.replace({
                search: serializeSearchParams(selectedOptions, { skipEmptyString: true }),
            });
        },
        [history]
    );

    const itemsPerPage = selectedOptions.limit;
    const page = selectedOptions.skip / itemsPerPage;

    return (
        <section className="uk-section uk-section-default">
            <div className="uk-container">
                <div className="uk-grid uk-grid-small" data-uk-grid="">
                    <OptionsSidebar>
                        <FilterBoxen
                            selectedOptions={selectedOptions}
                            onSelectedOptionsChange={onSelectedOptionsChange}
                            completeWheelOptionsResponse={completeWheelOptionsResponse}
                        />
                    </OptionsSidebar>
                    <div className="uk-width-expand">
                        <ReduxSuspense response={completeWheelsResponse} fallback={<InlineSpinner />} error={null}>
                            {({ completeWheels, count }) => (
                                <>
                                    <CompleteWheelList completeWheels={completeWheels} />
                                    <Pagination
                                        itemCount={count}
                                        itemsPerPage={itemsPerPage}
                                        selectedPage={page + 1}
                                        onPageChange={page => {
                                            window.scrollTo(0, 0);
                                            history.replace({
                                                search: serializeSearchParams(
                                                    { ...selectedOptions, skip: (page - 1) * itemsPerPage },
                                                    { skipEmptyString: true }
                                                ),
                                            });
                                        }}
                                    />
                                </>
                            )}
                        </ReduxSuspense>
                        <ProductListFooter />
                    </div>
                </div>
            </div>
        </section>
    );
}

function InnerLoadCompleteWheels({ selectedOptions, completeWheelsResponse, completeWheelOptionsResponse }) {
    const additionalCrumbs = [
        { label: 'Reifen & Felgen', url: `/reifen-felgen` },
        {
            label: 'Komplettrad',
            url: `/komplettrad`,
        },
        { label: 'Komplettradauswahl' },
    ];

    return (
        <>
            <BreadCrumbSelector additionalCrumbs={additionalCrumbs} showOptionsSidebarToggle />
            <CompleteWheelsContent
                selectedOptions={selectedOptions}
                completeWheelsResponse={completeWheelsResponse}
                completeWheelOptionsResponse={completeWheelOptionsResponse}
            />
        </>
    );
}

const frontload = async ({ fetchCompleteWheelsIfNeeded, fetchCompleteWheelOptionsIfNeeded, selectedOptions }) => {
    await Promise.all([fetchCompleteWheelsIfNeeded({ selectedOptions }), fetchCompleteWheelOptionsIfNeeded({ selectedOptions })]);
};
const mapDispatchToProps = { fetchCompleteWheelsIfNeeded, fetchCompleteWheelOptionsIfNeeded };
const mapStateToProps = (state, { selectedOptions }) => {
    return {
        completeWheelsResponse: completeWheelsResponseSelector(state, { selectedOptions }),
        completeWheelOptionsResponse: completeWheelOptionsResponseSelector(state, {
            selectedOptions,
        }),
    };
};
const LoadCompleteWheels = connect(
    mapStateToProps,
    mapDispatchToProps
)(frontloadConnect(frontload, { onUpdate: true })(InnerLoadCompleteWheels));

const convertMultipleChoiceFilter = filter => compact(castArray(filter));

function CompleteWheels() {
    const { search } = useLocation();

    const selectedOptions = useMemo(() => {
        let {
            hsn = '',
            tsn = '',
            breite = '',
            hoehe = '',
            durchmesser = '',
            geschwindigkeitsindex,
            tragfaehigkeit,
            preisAb,
            preisBis,
            reifenHersteller,
            felgenHersteller,
            skip = '0',
            limit = '20',
        } = parseSearch(search);

        geschwindigkeitsindex = convertMultipleChoiceFilter(geschwindigkeitsindex);
        tragfaehigkeit = convertMultipleChoiceFilter(tragfaehigkeit);
        reifenHersteller = convertMultipleChoiceFilter(reifenHersteller);
        felgenHersteller = convertMultipleChoiceFilter(felgenHersteller);

        return {
            hsn,
            tsn,
            breite,
            hoehe,
            durchmesser,
            reifenHersteller,
            felgenHersteller,
            geschwindigkeitsindex,
            tragfaehigkeit,
            preisAb,
            preisBis,
            skip,
            limit,
        };
    }, [search]);

    return <LoadCompleteWheels selectedOptions={selectedOptions} />;
}

export default CompleteWheels;
